import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { BrickSettingsService, GrafanaDashboardViewerBrickSettingRemoteDto } from 'src/generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { ChangeAgentId, ChangeDashboardId, ChangeUseBrowserTimezoneAsTargetTimezone } from './grafana-dashboard-viewer-brick-settings.actions';

export const FOLLOW_SELECTED_AGENT = -1;
export const INDEPENDENT_FROM_SELECTED_AGENT = 0;

export interface GrafanaDashboardViewerBrickSettingsStateModel extends IBrickSettingStateModel<GrafanaDashboardViewerBrickSettingRemoteDto> {}
function getDefaultSpecificBrickSetting(): GrafanaDashboardViewerBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      dashboardId: null,
      agentId: INDEPENDENT_FROM_SELECTED_AGENT,
      useBrowserTimezoneAsTargetTimezone: false,
   };
}

@Injectable()
@State<GrafanaDashboardViewerBrickSettingsStateModel>({
   name: 'grafanaDashboardViewerBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
export class GrafanaDashboardViewerSettingsState extends SpecificBrickSettingsStateBase<GrafanaDashboardViewerBrickSettingRemoteDto>(
   getDefaultSpecificBrickSetting
) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store) {
      super(store);
   }

   @Selector([GrafanaDashboardViewerSettingsState, GrafanaDashboardViewerSettingsState.getSettingModelFn])
   public static getDashboardSettingsFn(
      state: GrafanaDashboardViewerBrickSettingsStateModel,
      getSettingModelFn: (state, brickInstanceId) => GrafanaDashboardViewerBrickSettingRemoteDto
   ) {
      return (brickInstanceId: string) => {
         return getSettingModelFn(state, brickInstanceId);
      };
   }

   @Action(ChangeDashboardId)
   @ImmutableContext()
   public changeDashboardId(ctx: StateContext<GrafanaDashboardViewerBrickSettingsStateModel>, action: ChangeDashboardId) {
      ctx.setState((state: GrafanaDashboardViewerBrickSettingsStateModel) => {
         const brickSettingModel = GrafanaDashboardViewerSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.dashboardId = action.dashboardId;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeAgentId)
   @ImmutableContext()
   public changeAgentId(ctx: StateContext<GrafanaDashboardViewerBrickSettingsStateModel>, action: ChangeAgentId) {
      ctx.setState((state: GrafanaDashboardViewerBrickSettingsStateModel) => {
         const brickSettingModel = GrafanaDashboardViewerSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.agentId = action.agentId;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeUseBrowserTimezoneAsTargetTimezone)
   @ImmutableContext()
   public changeUseBrowserTimezoneAsTargetTimezone(
      ctx: StateContext<GrafanaDashboardViewerBrickSettingsStateModel>,
      action: ChangeUseBrowserTimezoneAsTargetTimezone
   ) {
      ctx.setState((state: GrafanaDashboardViewerBrickSettingsStateModel) => {
         const brickSettingModel = GrafanaDashboardViewerSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.useBrowserTimezoneAsTargetTimezone = action.useBrowserTimezoneAsTargetTimezone;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: GrafanaDashboardViewerBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeGrafanaDashboardViewerBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
